<template>
  <div>
    <div v-if="commentList.length" class="mb-4">
      <div
        class="text-uppercase font-weight-bold text-disabled font-size-13 mb-3">
        Коментарі · {{ commentList.length }}
      </div>

      <div class="d-flex flex-column ga-3">
        <comment-item
          v-for="comment in commentList"
          :key="comment.id"
          :comment="comment"
          :disabled="disabled || userId !== comment.userId"></comment-item>
      </div>
    </div>

    <empty-section
      v-else
      description="Будьте першим, хто залишить коментар"
      title="Коментарів ще немає"></empty-section>

    <v-card v-if="!disabled" border class="bg-grey-lighten-5">
      <v-card-text>
        <b-textarea
          v-model="newComment"
          class="bg-white mb-3"
          hide-details
          placeholder="Напишіть коментар..."
          rows="3"></b-textarea>

        <div
          v-if="attachmentsEnabled && documents.length"
          class="d-flex flex-wrap ga-2 mb-3">
          <v-chip
            v-for="file in documents"
            :key="file.path"
            border
            closable
            color="blue-darken-2"
            size="small"
            variant="tonal"
            @click:close="removeDocument(file)">
            <v-icon class="mr-1" icon="mdi-file-outline"></v-icon>
            <span class="font-weight-medium">{{ file.name }}</span>
          </v-chip>
        </div>

        <div class="d-flex justify-end ga-2">
          <v-btn
            v-if="attachmentsEnabled"
            :loading="$loading.isLoading(uploadLoaderName)"
            border
            color="white"
            density="comfortable"
            icon
            @click="selectDocuments">
            <v-icon size="small" style="transform: rotate(45deg)">
              mdi-paperclip
            </v-icon>
          </v-btn>
          <v-btn :loading="$loading.isLoading('commentCreate')" @click="send">
            Надіслати
          </v-btn>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts" setup>
import { BTextarea } from 'best-modules/components'
import { useStore } from 'vuex'
import { computed, ref } from 'vue'
import CommentItem from './CommentItem.vue'
import { useComments } from './index'
import EmptySection from '@/components/EmptySection.vue'
import { selectFile } from '@/components/filesUploader/selectFile'
import {
  uploadFiles,
  type MainFolder,
} from '@/components/filesUploader/uploadFiles'
import { File } from '@/utils/types/helpers'

defineOptions({
  name: 'Comments',
})

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    showDelete?: boolean
    attachMainFolder?: MainFolder
    attachNameFolder?: string | number
  }>(),
  {
    disabled: false,
    showDelete: true,
    attachMainFolder: undefined,
    attachNameFolder: undefined,
  }
)

const store = useStore()
const userId = computed(() => store.state.user.userData?.id)

const { commentList, newComment, commentCreate } = useComments()

const documents = ref<File[]>([])

const attachmentsEnabled = computed<boolean>(() => {
  return !!props.attachMainFolder && props.attachNameFolder != null
})

const uploadLoaderName = computed(() => {
  return `uploadFile_${props.attachMainFolder}_${props.attachNameFolder}`
})

const selectDocuments = () => {
  return selectFile({ multiple: true }).then(({ blob }) => {
    return uploadFiles({
      files: blob,
      mainFolder: props.attachMainFolder as MainFolder,
      nameFolder: String(props.attachNameFolder),
    }).then(res => {
      documents.value = [...documents.value, ...res.files]
    })
  })
}

const removeDocument = (file: File) => {
  documents.value = documents.value.filter(item => item.path !== file.path)
}

const send = () => {
  return commentCreate(documents.value).then(() => {
    documents.value = []
  })
}
</script>
